<template>
  <form name="notification-banners-form" @submit.prevent="$emit('save')">
    <BLayoutExpansion title="Основное" class="my-4">
      <v-row dense class="mb-2">
        <v-col cols="12" lg="8">
          <BEditor
            v-model="form.body"
            validate="required"
            data-vv-as="Тело"
            :disabled="isView"
            :skeleton="skeleton"
            name="body"
            placeholder="Введите тело*"
            min-height="200px"
            @input="$validator.validate('body')"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" md="auto">
          <BTextFieldDate
            v-model="form.start_show_at"
            validate="required"
            :skeleton="skeleton"
            :max="form.end_show_at"
            :disabled="isView"
            label="Начало показа"
            placeholder="Введите дату"
            name="start_show_at"
            datetime
          />
        </v-col>

        <v-col cols="12" md="auto">
          <BTextFieldDate
            v-model="form.end_show_at"
            validate="required"
            :skeleton="skeleton"
            :min="form.start_show_at"
            :disabled="isView"
            label="Окончание показа"
            placeholder="Введите дату"
            name="end_show_at"
            datetime
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col md="8" lg="6">
          <BAutocompleteCommon
            v-model="form.topic_ids"
            validate="required"
            :skeleton="skeleton"
            :disabled="isView"
            :return-object="false"
            $type="topic"
            multiple
            small-chips
            name="topic_ids"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="auto" class="my-2">
          <v-checkbox
            v-model="form.is_active"
            :disabled="isView || true"
            label="Активен?"
            dense
            hide-details
            class="ma-0 pa-0"
            name="is_active"
          />
        </v-col>
      </v-row>

      <v-row v-if="!isCreate" dense>
        <v-col :cols="!skeleton && 'auto'">
          <BTextField
            :value="data.created_at | dateFormatter"
            :skeleton="skeleton"
            label="Создан"
            disabled
          />
        </v-col>

        <v-col :cols="!skeleton && 'auto'">
          <BTextField
            :value="data.updated_at | dateFormatter"
            :skeleton="skeleton"
            label="Обновлен"
            disabled
          />
        </v-col>
      </v-row>
    </BLayoutExpansion>

    <BLayoutExpansion title="Изображение" class="mb-4">
      <v-row dense>
        <v-col cols="12" lg="8">
          <NotificationBannersFileInput
            :value="form.image"
            label="Изображение"
            :disabled="isView"
            :skeleton="skeleton"
            name="image_id"
            messages="Формат прикрепляемого файла - jpg, png. Максимальный размер файла - 1 Мб.
              Соотношение сторон - 1:1."
            @input="onImageInput"
            @click:clear="onImageClear"
          />
        </v-col>
      </v-row>
    </BLayoutExpansion>

    <BLayoutExpansion title="Кнопка">
      <template #header>
        <b>Кнопка</b>
        <v-spacer />
        <v-btn
          outlined
          dense
          small
          style="margin: -12px 0;"
          @click.stop="onAddPublicationLink"
          v-text="'Выбрать публикацию'"
        />
      </template>

      <v-row dense>
        <v-col cols="12" lg="8">
          <BTextField
            v-model="form.btn_title"
            :validate="{ required: !!form.btn_link }"
            label="Заголовок кнопки"
            :disabled="isView"
            :skeleton="skeleton"
            clearable
            name="btn_title"
          />
        </v-col>

        <v-col cols="12">
          <BTextField
            v-model="form.btn_link"
            :validate="{ required: !!form.btn_title }"
            label="Ссылка кнопки"
            :disabled="isView"
            :skeleton="skeleton"
            clearable
            prepend-inner-icon="mdi-link-variant"
            name="btn_link"
          />
        </v-col>
      </v-row>
    </BLayoutExpansion>

    <button type="submit" class="d-none" />
  </form>
</template>

<script>
import { createNotificationBannersPublicationSelectModal } from '../utils'

import BLayoutForm from '@/components/layouts/BLayoutForm.vue'
import BLayoutExpansion from '@/components/layouts/BLayoutExpansion.vue'
import BTextField from '@/components/forms/BTextField.vue'
import BTextFieldDate from '@/components/forms/BTextFieldDate.vue'
import BAutocompleteCommon from '@/components/forms/BAutocompleteCommon.vue'
import NotificationBannersFileInput from './NotificationBannersFileInput.vue'


const createDefaultData = () => ({
  // id
  // position
  is_active: false,
  body: '',
  btn_title: '',
  btn_link: '',
  start_show_at: null,
  end_show_at: null,
  image_id: null,
  image: null,
  topic_ids: [],
  // created_at
  // updated_at
})

const BEditor = () => import(
  /* webpackChunkName: "BEditor" */
  '@/components/forms/BEditor.vue'
)

export default {
  name: 'NotificationBannersForm',
  components: {
    BLayoutExpansion,
    BTextField,
    BEditor,
    BTextFieldDate,
    BAutocompleteCommon,
    NotificationBannersFileInput,
  },
  extends: BLayoutForm,
  props: {
    skeleton: Boolean,
    data: {
      type: Object,
      required: true,
      default: createDefaultData,
    },
  },
  data: () => ({
    isImageDropped: false,
  }),
  methods: {
    async onAddPublicationLink() {
      const propData = {}
      const modal = await createNotificationBannersPublicationSelectModal(this, propData)
      const result = await modal.open()
      if (!result) return
      this.form.btn_title = this.form.btn_title || 'Ссылка на публикацию'
      this.form.btn_link = result.link
    },
    async onSubmit() {
      const result = await this.$validator.validate()

      if (!result) {
        this.$notice.validatorFormError(this)
        const message = `Error submit: ${this.$options.name}`
        const error = new Error(message)
        return Promise.reject(error)
      }

      const { form, isImageDropped } = this

      const data = {
        ...form,

        image_id: isImageDropped ? void 0 : (form.image?.id || form.image_id),
        image: void 0,
      }

      this.$emit('submit', data)

      return data
    },
    onImageInput(data) {
      this.form.image = data
      this.isImageDropped = !data
    },
    onImageClear() {
      this.isImageDropped = true
    },
  },
}
</script>
